import React, { ReactNode } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
  // Denser header for narrow/mobile layouts (the viewer's "overlay" mode). Defaults to
  // false so every existing consumer (desktop viewer header, worklist header) that never
  // passes this prop renders exactly as before. Explicitly destructured (not left in
  // `...props`) so it can never leak onto <NavBar> as a stray DOM attribute.
  compact?: boolean;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  compact = false,
  ...props
}: HeaderProps): ReactNode {
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className="px-2.5 py-2">
          <div
            className={classNames(
              'flex min-h-[34px] items-center justify-between border-b border-white/10 pb-2',
              compact ? 'gap-2' : 'gap-6'
            )}
          >
            <div
              className={classNames(
                'flex flex-shrink-0 items-center gap-2.5',
                !compact && 'min-w-[220px]',
                isReturnEnabled && 'cursor-pointer'
              )}
              onClick={onClickReturn}
              data-cy="return-to-work-list"
            >
              {isReturnEnabled && <Icons.ArrowLeft className="h-5 w-5 text-[#f1e7dd]" />}
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  {WhiteLabeling?.createLogoComponentFn?.(React, props) || <Icons.OHIFLogo />}
                </div>
                <div className="flex flex-col leading-none">
                  <span className="text-[15px] font-medium tracking-[0.18em] text-white">
                    LUNNON
                  </span>
                  {!compact && (
                    <span className="text-[9px] uppercase tracking-[0.3em] text-white/55">
                      PACS Viewer
                    </span>
                  )}
                </div>
              </div>
            </div>

            {Secondary && <div className="hidden min-w-0 flex-1 justify-center lg:flex">{Secondary}</div>}

            <div
              className={classNames(
                'flex items-center gap-2 select-none',
                compact ? 'min-w-0' : 'flex-shrink-0'
              )}
            >
              <div className="flex flex-shrink-0 items-center gap-0.5">{UndoRedo}</div>
              {/* min-w-0 + overflow-hidden let this shrink below its content's natural width
                  (and clip rather than push the row wider) once the cluster above can also
                  shrink (compact mode); inert on desktop where the cluster never shrinks. */}
              <div className="min-w-0 flex-1 overflow-hidden">{PatientInfo}</div>
              <div className="flex-shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-full w-full text-white/75 hover:bg-white/10 hover:text-white"
                    >
                      <Icons.GearSettings />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {menuOptions.map((option, index) => {
                      const IconComponent = option.icon
                        ? Icons[option.icon as keyof typeof Icons]
                        : null;
                      return (
                        <DropdownMenuItem
                          key={index}
                          onSelect={option.onClick}
                          className="flex items-center gap-2 py-2"
                        >
                          {IconComponent && (
                            <span className="flex h-4 w-4 items-center justify-center">
                              <Icons.ByName name={option.icon} />
                            </span>
                          )}
                          <span className="flex-1">{option.title}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          <div
            className={classNames(
              'flex min-h-[66px] items-end justify-center overflow-x-auto overflow-y-hidden pt-1.5',
              compact && 'compact-toolbar-scroll'
            )}
          >
            <div className="flex min-w-max items-end justify-center gap-2.5 md:gap-3">
              {children}
            </div>
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
